import React, { useMemo, useState } from 'react'
import { Controller, SubmitErrorHandler, SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { Autocomplete, TextField } from '@mui/material'
import { Pencil2Icon, TrashIcon } from '@radix-ui/react-icons'
import { Merchant } from 'Utils/types/JOONAdmin/merchant'
import { toast } from 'react-toastify'
import { FlexRow, spacingLG, spacingMD, spacingSM, spacingXXS } from 'Utils/styles/spacing'
import { StyledButton } from 'Components/Buttons/StyledButton'
import { ModalBody } from 'Components/Modal/ModalBody'
import { ControlledSelect } from 'Components/Inputs/ControlledSelect'
import styled from 'styled-components'
import { DarkGreenText, MediumBoldText, telegrafFont, ThinDarkGreenText } from 'Utils/styles/text'
import { frostedGlass } from 'Utils/styles/colors'
import { useConfirmationModalContext } from 'Hooks/modals/useConfirmationModal'
import { useUpsertMerchantToProgramQuery } from 'Hooks/api/JOONAdmin/useUpsertMerchantToProgramQuery'
import { EmployerBenefitProgram } from 'Utils/types/benefitProgram'
import { useRemoveMerchantFromAllowListQuery } from 'Hooks/api/JOONAdmin/useRemoveMerchantFromAllowListQuery'
import { useRemoveMerchantFromBlockListQuery } from 'Hooks/api/JOONAdmin/useRemoveMerchantFromBlockListQuery'
import { useGetMerchantsQuery } from 'Hooks/api/JOONAdmin/useGetMerchantsQuery'
import { useGetBenefitProgramMerchantsQuery } from 'Hooks/api/JOONAdmin/useGetBenefitProgramMerchantsQuery'
import { ComponentLoading } from 'Components/CircularProgress/ComponentLoading'
import debounce from 'lodash.debounce'

type MerchantFormProps = {
  program: EmployerBenefitProgram
  allowList?: Merchant[]
  blockList?: Merchant[]
}

enum ProgramAction {
  Allow = 'Allow',
  Block = 'Block'
}

const actionOptions = [
  { label: ProgramAction.Allow, value: ProgramAction.Allow },
  { label: ProgramAction.Block, value: ProgramAction.Block }
]

export type MerchantFormValues = {
  selectedMerchant?: Merchant['id']
  merchantName?: string
}

const ListContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
  gap: 1%;
`

const Column = styled.div`
  flex: 1;
  width: 49%;
  background-color: ${frostedGlass};
  padding: ${spacingSM};
  border-radius: 8px;
`

const MerchantName = styled(DarkGreenText)`
  line-height: 22px;
  font-size: 15px;
`

const MerchantItemContainer = styled(FlexRow)`
  justify-content: space-between;
  gap: ${spacingXXS};
`

const ActionButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${spacingXXS};
  padding: ${spacingXXS};
  &:hover {
    cursor: pointer;
    background-color: rgba(0, 0, 0, 0.05);
  }
`

const ActionContainer = styled.div`
  display: flex;
  align-items: center;
`

const Header = styled(MediumBoldText)`
  ${telegrafFont};
`

export const MerchantForm: React.FC<MerchantFormProps> = ({ program }) => {
  const [action, setAction] = useState<ProgramAction>(ProgramAction.Block)
  const [categoryId, setCategoryId] = useState<string>()
  const [searchTerm, setSearchTerm] = useState<string>('')

  const categoryOptions = useMemo(
    () =>
      program.categories.map((category) => ({
        label: category.name,
        value: `${category.id}`
      })),
    [program.categories]
  )

  const debouncedSetSearchTerm = useMemo(() => debounce(setSearchTerm, 300), [])

  const { data: merchants } = useGetMerchantsQuery({ searchText: searchTerm })
  const { data: benefitProgramMerchants, isLoading: benefitProgramMerchantsLoading } =
    useGetBenefitProgramMerchantsQuery(program.id)

  const sortedMerchants = useMemo(() => {
    return (merchants?.data || []).sort((a, b) => -b.name[0].localeCompare(a.name[0]))
  }, [merchants])

  const { mutate: upsertMerchantToProgramMutation } = useUpsertMerchantToProgramQuery()
  const { mutate: removeMerchantFromAllowListMutation } = useRemoveMerchantFromAllowListQuery()
  const { mutate: removeMerchantFromBlockListMutation } = useRemoveMerchantFromBlockListQuery()

  const { showModal } = useConfirmationModalContext()

  const { control, handleSubmit, setValue } = useForm<MerchantFormValues>()

  const handleSubmission: SubmitHandler<MerchantFormValues> = async (data) => {
    const result = await showModal('Save Merchant', 'Are you sure you want to save this merchant?')

    if (result) {
      upsertMerchantToProgramMutation({
        ...data,
        merchantName: data.selectedMerchant ? undefined : searchTerm,
        action,
        programId: program.id,
        categoryId: action === ProgramAction.Allow ? parseInt(categoryId || '0', 10) : undefined
      })
    }
  }

  const handleRemoveFromAllowList = (merchantId: Merchant['id']) => {
    removeMerchantFromAllowListMutation({ programId: program.id, merchantId })
  }

  const handleRemoveFromBlockList = (merchantId: Merchant['id']) => {
    removeMerchantFromBlockListMutation({ programId: program.id, merchantId })
  }

  const handleEditMerchant = (merchant: Merchant) => {
    setAction(ProgramAction.Allow) // Edit is only available for allow list
    setValue('selectedMerchant', merchant.id)

    if (merchant.basicCategory) {
      setCategoryId(merchant.basicCategory.id.toString())
    } else {
      setCategoryId(undefined)
    }
  }

  const availableMerchants = useMemo(
    () => [...(merchants?.data || []), ...(benefitProgramMerchants?.allowList || [])],
    [merchants, benefitProgramMerchants]
  )

  const handleError: SubmitErrorHandler<MerchantFormValues> = (error) => {
    toast.error(error.selectedMerchant?.message)
  }

  const selectedMerchant = useWatch({ control, name: 'selectedMerchant' })
  const formInvalid = !selectedMerchant || (action === ProgramAction.Allow && !categoryId)
  return (
    <ModalBody style={{ padding: `${spacingMD} 0` }}>
      <form onSubmit={handleSubmit(handleSubmission, handleError)}>
        <FlexRow gap={spacingLG} style={{ alignItems: 'flex-end' }}>
          <Controller
            control={control}
            name="selectedMerchant"
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Autocomplete
                id="merchant-auto-complete"
                options={sortedMerchants ?? []}
                freeSolo
                groupBy={(option) => option.name[0].toUpperCase()}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
                value={availableMerchants.find((merchant) => merchant.id === value) || null}
                onChange={(_, newValue) => {
                  if (typeof newValue === 'string') {
                    setValue('merchantName', newValue)
                    setValue('selectedMerchant', undefined)
                  } else {
                    setValue('merchantName', undefined)
                    onChange(newValue?.id)
                  }
                }}
                onInputChange={(_, newInputValue) => {
                  debouncedSetSearchTerm(newInputValue)
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Merchants"
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
                sx={{ minWidth: '250px', flex: 1 }}
              />
            )}
          />
          <ControlledSelect
            label="Action"
            options={actionOptions}
            defaultValue={action}
            setControlValue={setAction}
          />
          {action === ProgramAction.Allow && (
            <ControlledSelect
              label="Category"
              options={categoryOptions}
              defaultValue={categoryId}
              setControlValue={(v) => {
                setCategoryId(v)
              }}
            />
          )}
          <StyledButton primary id="save.label" isLoading={false} disabled={formInvalid} />
        </FlexRow>
      </form>
      <ListContainer>
        <Column>
          <Header>Allow List</Header>
          <ul>
            {benefitProgramMerchantsLoading ? (
              <ComponentLoading />
            ) : (
              (benefitProgramMerchants?.allowList || []).map((merchant) => (
                <li key={merchant.id}>
                  <MerchantItemContainer>
                    <MerchantName>
                      {merchant.name}
                      {merchant.basicCategory && (
                        <ThinDarkGreenText> ({merchant.basicCategory.name})</ThinDarkGreenText>
                      )}
                    </MerchantName>
                    <ActionContainer>
                      <ActionButton
                        onClick={() => handleEditMerchant(merchant)}
                        title="Edit merchant"
                      >
                        <Pencil2Icon />
                      </ActionButton>
                      <ActionButton
                        onClick={() => handleRemoveFromAllowList(merchant.id)}
                        title="Remove merchant from allow list"
                      >
                        <TrashIcon />
                      </ActionButton>
                    </ActionContainer>
                  </MerchantItemContainer>
                </li>
              ))
            )}
          </ul>
        </Column>

        <Column>
          <Header>Block List</Header>
          <ul>
            {benefitProgramMerchantsLoading ? (
              <ComponentLoading />
            ) : (
              (benefitProgramMerchants?.blockList || []).map((merchant) => (
                <li key={merchant.id}>
                  <MerchantItemContainer>
                    <MerchantName>{merchant.name}</MerchantName>
                    <ActionContainer>
                      <ActionButton
                        onClick={() => handleRemoveFromBlockList(merchant.id)}
                        title="Remove merchant from block list"
                      >
                        <TrashIcon />
                      </ActionButton>
                    </ActionContainer>
                  </MerchantItemContainer>
                </li>
              ))
            )}
          </ul>
        </Column>
      </ListContainer>
    </ModalBody>
  )
}
